import { useEffect, useRef, useState } from 'react';
import { ApartmentOutlined, CloseOutlined, DeleteOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { Checkbox, DatePicker } from 'antd';
import dayjs from 'dayjs';

import { HanMucHoacCauGoc } from '../../../../components/shared';
import { Button, Dialog, Select, Skeleton, Textarea } from '../../../../components/ui';
import { useLoi } from '../../../../hooks/useLoi';
import { maLoi } from '../../../../lib/errors';
import { useT } from '../../../../i18n';
import { cn } from '../../../../lib/cn';
import { ngayVN } from '../../../../lib/dinh-dang';
import { NHAN_VAI_TRO } from '../../constants';
import { useGoiYPhanRa, useNguoiTrungTen, useTaoViecCon } from '../../hooks/useNhiemVu';
import { DINH_DANG_API, DINH_DANG_NGAY, donViAiChon, khopDonVi, thanhNguoiThamGia } from '../../nghiep-vu/nhiem-vu';
import type { DonViDanhBa, GoiYViecCon, NguoiThamGiaVao, NutNhiemVu, ViecConVao } from '../../types-nhiem-vu';
import { IconAi } from './IconAi';
import { UserAvatar } from './UserAvatar';
import { UserSearchSelect } from './UserSearchSelect';

const RONG_HOP = 1080;
const SO_DONG_CHO = 4;

type VaiTroCon = 'CHU_TRI' | 'PHOI_HOP';

interface Dong {
  key: string;
  include: boolean;
  title: string;
  deadline: string;
  /** `null` = người dùng chưa đụng tới: lấy theo đơn vị AI chọn khớp với cơ cấu tổ chức. */
  leadOrgId: string | null;
  /** `undefined` = chưa đụng tới: lấy người trùng tên đầy đủ trong danh bạ (nếu AI chọn cá nhân). */
  person: NguoiThamGiaVao | null | undefined;
  role: VaiTroCon;
  processingUnitId: string | null;
  aiAssignee: string;
  aiType: GoiYViecCon['assignee_type'];
  /** Đơn vị theo Vai trò các cấp — hiện thành thẻ khi chưa khớp được cơ cấu tổ chức. */
  aiUnit: string;
}

let soDong = 0;
const khoaMoi = () => `dong-${++soDong}`;

function tuGoiY(g: GoiYViecCon): Dong {
  return {
    key: khoaMoi(),
    include: true,
    title: g.title,
    deadline: g.deadline,
    leadOrgId: null,
    person: undefined,
    role: g.role,
    processingUnitId: g.processing_unit_id,
    aiAssignee: g.assignee_name,
    aiType: g.assignee_type,
    aiUnit: donViAiChon(g),
  };
}

function dongTrong(cha: NutNhiemVu): Dong {
  return {
    key: khoaMoi(),
    include: true,
    title: '',
    deadline: cha.deadline ?? '',
    leadOrgId: cha.lead_org_id ?? '',
    person: null,
    role: 'CHU_TRI',
    processingUnitId: null,
    aiAssignee: '',
    aiType: null,
    aiUnit: '',
  };
}

interface Props {
  parent: NutNhiemVu;
  orgs: DonViDanhBa[];
  onClose: () => void;
}

/**
 * AI gợi ý việc con (chưa lưu) → người dùng sửa, bỏ chọn, thêm dòng → tạo cả lô trong một giao dịch.
 * Chỗ gọi đặt `key={parent.id}` để mỗi việc cha một hộp mới.
 */
export function DecomposeDialog({ parent, orgs, onClose }: Props) {
  const { t } = useT();
  const dichLoi = useLoi();
  const goiY = useGoiYPhanRa();
  const tao = useTaoViecCon();
  const [rows, setRows] = useState<Dong[]>([]);
  const [loi, setLoi] = useState<string | null>(null);

  const chay = () => {
    setRows([]);
    goiY.mutate(parent.id, { onSuccess: (kq) => setRows(kq.items.map(tuGoiY)) });
  };
  // Mở hộp là gọi AI ngay, như bản cũ; chỉ chạy một lần cho mỗi việc cha.
  const daChay = useRef(false);
  useEffect(() => {
    if (daChay.current) return;
    daChay.current = true;
    chay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const khop = useNguoiTrungTen(rows.filter((r) => r.aiType === 'CA_NHAN').map((r) => r.aiAssignee));

  const donViCua = (r: Dong) =>
    r.leadOrgId ?? (r.aiUnit ? khopDonVi(r.aiUnit, orgs) : (parent.lead_org_id ?? ''));
  const nguoiCua = (r: Dong): NguoiThamGiaVao | null => {
    if (r.person !== undefined) return r.person;
    if (r.aiType !== 'CA_NHAN') return null;
    const u = khop.theoTen.get(r.aiAssignee);
    return u ? thanhNguoiThamGia(u, orgs, r.role) : null;
  };

  const sua = (key: string, thay: Partial<Dong>) => setRows((cu) => cu.map((r) => (r.key === key ? { ...r, ...thay } : r)));
  const chon = rows.filter((r) => r.include);

  const gui = () => {
    if (chon.length === 0) return setLoi(t('vanBanNhiemVu.phanRa.chuaChon'));
    if (chon.some((r) => !r.title.trim())) return setLoi(t('vanBanNhiemVu.phanRa.noiDungThieu'));
    if (parent.deadline && chon.some((r) => r.deadline && r.deadline > (parent.deadline ?? ''))) {
      return setLoi(t('vanBanNhiemVu.phanRa.hanVuot', { ngay: ngayVN(parent.deadline) }));
    }
    setLoi(null);
    const con: ViecConVao[] = chon.map((r) => {
      const dv = donViCua(r);
      const nguoi = nguoiCua(r);
      return {
        title: r.title.trim(),
        deadline: r.deadline || null,
        lead_org_id: dv || null,
        lead_org_name: orgs.find((o) => o.id === dv)?.name ?? null,
        processing_unit_id: r.processingUnitId,
        participants: nguoi ? [{ ...nguoi, role: r.role }] : [],
      };
    });
    tao.mutate({ id: parent.id, con }, { onSuccess: onClose });
  };

  return (
    <Dialog
      open
      onClose={() => !tao.isPending && onClose()}
      width={RONG_HOP}
      destroyOnHidden
      styles={{ body: { maxHeight: '65vh', overflowY: 'auto' } }}
      title={
        <span className="flex items-center gap-2">
          <IconAi className="text-primary-600" />
          {t('vanBanNhiemVu.phanRa.tieuDe')}
        </span>
      }
      footer={
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-2">
            <Button variant="ghost" icon={<ReloadOutlined />} disabled={goiY.isPending} onClick={chay}>
              {t('vanBanNhiemVu.phanRa.goiYLai')}
            </Button>
            <Button
              variant="ghost"
              icon={<PlusOutlined />}
              disabled={goiY.isPending}
              onClick={() => setRows((cu) => [...cu, dongTrong(parent)])}
            >
              {t('vanBanNhiemVu.phanRa.themDong')}
            </Button>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={tao.isPending}>
              {t('chung.huy')}
            </Button>
            <Button variant="primary" loading={tao.isPending} disabled={goiY.isPending || chon.length === 0} onClick={gui}>
              {tao.isPending ? t('chung.dangLuu') : t('vanBanNhiemVu.phanRa.nut', { so: chon.length })}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-3 pt-1">
        <p className="rounded-lg bg-subtle px-3 py-2 text-sm text-content-secondary">
          {t('vanBanNhiemVu.phanRa.cha', { ten: parent.title })}
          {parent.deadline && <span className="text-content-muted"> · {ngayVN(parent.deadline)}</span>}
        </p>
        {goiY.isPending ? (
          <div className="space-y-2">
            <p className="flex items-center gap-2 text-sm text-content-secondary">
              <IconAi className="text-primary-600" />
              {t('vanBanNhiemVu.phanRa.dangGoiY')}{' '}
              <span className="text-content-muted">{t('vanBanNhiemVu.phanRa.dangGoiYMo')}</span>
            </p>
            {Array.from({ length: SO_DONG_CHO }, (_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        ) : goiY.isError ? (
          <HanMucHoacCauGoc
            cau={goiY.error.message}
            maLoi={maLoi(goiY.error)}
            bienThe="gon"
            nhanThuLai={t('vanBanNhiemVu.phanRa.goiYLai')}
            nutThuLai={
              <Button size="sm" variant="outline" icon={<ReloadOutlined />} onClick={chay}>
                {t('vanBanNhiemVu.phanRa.goiYLai')}
              </Button>
            }
          >
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-default px-3 py-2">
              <p className="min-w-0 text-sm text-danger-700">{t('vanBanNhiemVu.phanRa.loi', { loi: dichLoi(goiY.error) })}</p>
              <Button size="sm" variant="outline" icon={<ReloadOutlined />} onClick={chay}>
                {t('vanBanNhiemVu.phanRa.goiYLai')}
              </Button>
            </div>
          </HanMucHoacCauGoc>
        ) : goiY.isSuccess && rows.length === 0 ? (
          <p className="text-sm text-content-muted">{t('vanBanNhiemVu.phanRa.rong')}</p>
        ) : goiY.data ? (
          <p className="text-xs text-content-muted">
            {goiY.data.used_attachment ? t('vanBanNhiemVu.phanRa.daDocTep') : t('vanBanNhiemVu.phanRa.khongCoTep')}
          </p>
        ) : null}
        {loi && (
          <p role="alert" className="text-sm text-danger-700">
            {loi}
          </p>
        )}
        {rows.length > 0 && (
          <ul className="space-y-2">
            {rows.map((r, i) => (
              <DongPhanRa
                key={r.key}
                so={i + 1}
                row={r}
                parent={parent}
                orgs={orgs}
                leadOrgId={donViCua(r)}
                person={nguoiCua(r)}
                matching={khop.dangKhop && r.aiType === 'CA_NHAN' && r.person === undefined}
                onChange={(thay) => sua(r.key, thay)}
                onRemove={() => setRows((cu) => cu.filter((x) => x.key !== r.key))}
              />
            ))}
          </ul>
        )}
      </div>
    </Dialog>
  );
}

function DongPhanRa({
  so,
  row,
  parent,
  orgs,
  leadOrgId,
  person,
  matching,
  onChange,
  onRemove,
}: {
  so: number;
  row: Dong;
  parent: NutNhiemVu;
  orgs: DonViDanhBa[];
  leadOrgId: string;
  person: NguoiThamGiaVao | null;
  matching: boolean;
  onChange: (thay: Partial<Dong>) => void;
  onRemove: () => void;
}) {
  const { t } = useT();
  const hanMax = parent.deadline ? dayjs(parent.deadline, DINH_DANG_API) : null;
  const vuot = Boolean(parent.deadline && row.deadline && row.deadline > parent.deadline);

  return (
    <li
      className={cn(
        'grid grid-cols-1 items-start gap-3 rounded-lg border border-default px-3 py-2.5 md:grid-cols-[1.5rem_minmax(0,1fr)_17rem_9.5rem_2rem]',
        row.include ? 'bg-surface' : 'bg-subtle opacity-60',
      )}
    >
      <Checkbox
        className="mt-1.5"
        aria-label={`${so}. ${t('vanBanNhiemVu.phanRa.chonViec')}`}
        checked={row.include}
        onChange={(e) => onChange({ include: e.target.checked })}
      />
      <div className="min-w-0 space-y-1">
        <Textarea
          aria-label={`${so}. ${t('vanBanNhiemVu.phanRa.noiDungGoiY')}`}
          autoSize={{ minRows: 1, maxRows: 4 }}
          maxLength={2000}
          placeholder={t('vanBanNhiemVu.phanRa.noiDungGoiY')}
          value={row.title}
          onChange={(e) => onChange({ title: e.target.value })}
        />
        {row.aiAssignee && (
          <p className={cn('text-2xs', row.aiType ? 'text-content-muted' : 'text-warning-700')}>
            {row.aiType
              ? t('vanBanNhiemVu.phanRa.aiChon', { ten: row.aiAssignee })
              : t('vanBanNhiemVu.phanRa.aiKhongKhop', { ten: row.aiAssignee })}
          </p>
        )}
      </div>
      <div className="min-w-0 space-y-1.5">
        <Select<string>
          aria-label={`${so}. ${t('vanBanNhiemVu.phanRa.donVi')}`}
          className="w-full"
          size="small"
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder={
            row.aiUnit ? (
              <span
                title={t('vanBanNhiemVu.phanRa.aiDonViMo')}
                className="inline-flex max-w-full items-center gap-1 rounded bg-primary-50 px-1.5 text-primary-700"
              >
                <ApartmentOutlined aria-hidden />
                <span className="truncate">{t('vanBanNhiemVu.phanRa.aiDonVi', { ten: row.aiUnit })}</span>
              </span>
            ) : (
              t('vanBanNhiemVu.phanRa.donVi')
            )
          }
          value={leadOrgId || undefined}
          onChange={(x) => onChange({ leadOrgId: x ?? '' })}
          options={orgs.map((o) => ({ value: o.id, label: o.name }))}
        />
        <NguoiCuaDong row={row} person={person} orgs={orgs} matching={matching} onChange={onChange} />
      </div>
      <div className="space-y-1">
        <DatePicker
          aria-label={`${so}. ${t('vanBanNhiemVu.phanRa.han')}`}
          size="small"
          className="w-full"
          format={DINH_DANG_NGAY}
          status={vuot ? 'error' : undefined}
          maxDate={hanMax ?? undefined}
          value={row.deadline ? dayjs(row.deadline, DINH_DANG_API) : null}
          onChange={(d) => onChange({ deadline: d ? d.format(DINH_DANG_API) : '' })}
        />
        {vuot && parent.deadline && (
          <p className="text-2xs text-danger-700">{t('vanBanNhiemVu.phanRa.hanVuot', { ngay: ngayVN(parent.deadline) })}</p>
        )}
      </div>
      <Button
        variant="ghost"
        size="sm"
        icon={<DeleteOutlined />}
        aria-label={t('vanBanNhiemVu.phanRa.boDong', { so })}
        onClick={onRemove}
      />
    </li>
  );
}

function NguoiCuaDong({
  row,
  person,
  orgs,
  matching,
  onChange,
}: {
  row: Dong;
  person: NguoiThamGiaVao | null;
  orgs: DonViDanhBa[];
  matching: boolean;
  onChange: (thay: Partial<Dong>) => void;
}) {
  const { t } = useT();
  if (person) {
    return (
      <div className="flex items-center gap-1.5">
        <UserAvatar name={person.user_name} className="h-6 w-6" />
        <span className="min-w-0 flex-1 truncate text-xs text-content-primary">{person.user_name}</span>
        <Select<VaiTroCon>
          size="small"
          aria-label={t('vanBanNhiemVu.themNhanh.vaiTro')}
          className="w-28 shrink-0"
          variant="borderless"
          value={row.role}
          onChange={(role) => onChange({ role, person: { ...person, role } })}
          options={(['CHU_TRI', 'PHOI_HOP'] as const).map((r) => ({ value: r, label: t(NHAN_VAI_TRO[r]) }))}
        />
        <button
          type="button"
          aria-label={t('vanBanNhiemVu.form.boNguoi', { ten: person.user_name })}
          onClick={() => onChange({ person: null })}
          className="text-content-muted hover:text-content-primary"
        >
          <CloseOutlined className="text-2xs" />
        </button>
      </div>
    );
  }
  return (
    <div className="space-y-0.5">
      <UserSearchSelect
        size="small"
        excludeIds={[]}
        onPick={(u) => onChange({ person: thanhNguoiThamGia(u, orgs, row.role) })}
      />
      {row.aiType === 'CA_NHAN' && row.person === undefined && (
        <p className="text-2xs text-content-muted">
          {matching ? t('vanBanNhiemVu.phanRa.dangKhop') : t('vanBanNhiemVu.phanRa.nguoiChuaKhop', { ten: row.aiAssignee })}
        </p>
      )}
    </div>
  );
}
